<template>
  <div
    class="tw:flex tw:flex-col tw:fixed tw:h-full tw:top-0 tw:right-0 tw:bottom-0 tw:bg-white tw:overflow-hidden tw:shadow-[var(--s-shadow-lg)] tw:duration-300 tw:transition-[width]"
    data-testid="sidebar"
    :style="{ width: sideBarWidth, zIndex: sideBarZIndex }"
  >
    <template v-if="!sideBarHideHeader">
      <div
        class="tw:px-4 tw:py-2 tw:flex tw:flex-nowrap tw:items-center tw:justify-between"
      >
        <div
          class="tw:m-0"
          data-testid="sidebar-title"
        >
          <h3>{{ sideBarTitle }}</h3>
          <span>{{ sideBarSubtitle }}</span>
        </div>
        <BaseButton
          v-if="!shouldHideCloseButton"
          :aria-label="getLocaleText('global.close')"
          circle
          class="tw:ml-2"
          data-testid="sidebar-close-button"
          icon="close"
          new-design
          new-icon
          @click="emit('close')"
        />
      </div>

      <BaseSeparator />
    </template>

    <div
      class="tw:flex-1 tw:min-h-0"
      data-testid="sidebar-body"
    >
      <slot>
        <Component
          :is="activeSideBar"
          v-if="activeSideBar"
          v-bind="sideBarData"
        />
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, markRaw, watch } from "vue";
import { useRoute } from "vue-router";

import { getLocaleText } from "shared/boot/i18n";
import { BaseButton, BaseSeparator } from "shared/components/base";
import { modalCount } from "shared/modals/ModalService";
import * as sideBars from "shared/modules/SideBar/components";
import {
  type SideBarComponent,
  useSideBarStore,
} from "shared/modules/SideBar/stores/useSideBarStore";
import { type Nullable } from "shared/types";

type ActiveSideBarComponent = (typeof sideBars)[SideBarComponent]["default"];

interface SideBarEmits {
  close: [];
}

const emit = defineEmits<SideBarEmits>();
const route = useRoute();
const sideBarStore = useSideBarStore();

const {
  sideBarTitle,
  sideBarSubtitle,
  sideBarComponent,
  sideBarData,
  sideBarHideHeader,
  sideBarWidth,
} = storeToRefs(sideBarStore);

const activeSideBar = computed<Nullable<ActiveSideBarComponent>>(() => {
  if (sideBarComponent.value)
    return markRaw(sideBars[sideBarComponent.value].default);

  return null;
});

const shouldHideCloseButton = computed<boolean>(() =>
  Boolean(sideBarData.value?.hideCloseButton)
);

// Sit above the modals container (z-index 8000) when the original modal is open,
// so the sidebar remains visible above its backdrop. When another modal opens
// from the sidebar, drop back so that top modal can sit above the sidebar.
const sideBarZIndex = computed<number>(() =>
  modalCount.value === 1 ? 8001 : 600
);

watch(
  () => route.name,
  () => {
    sideBarStore.hideSideBar();
  }
);
</script>
